import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react'
import FormCard from './FormCard'
import { useMyContext } from '../../context/MyContext';
import DutyOrbitCard from './DutyOrbitCard';
import IorCostCard from './IorCostCard';
import CompliancePassportCard from './CompliancePassportCard';
import GeneralComplianceCard from './GeneralComplianceCard';
import IorFormCard from './IorFormCard';
import { toast } from 'react-toastify';
import { CurrencyContext } from "../../context/CurrencyContext";
import { api, API_BASE_URL, API_DEPLOYED_URL } from "../../config/api"; // Added API_DEPLOYED_URL
import { calculateServiceValue } from '../../utils/estimateUtils';
import { getAlpha2Code } from '../../lib/helper/countryToAphlaCode';
import { getAllSuperAdminUsers } from "../../lib/user"; // Added for notifications
import LeadTimeCard from './LeadTimeCard';
import { useScopedFormState } from "../../context/TradeOSContext";

const Loader = () => (
    <div className="flex items-center justify-center space-x-2 p-4">
        <div className="w-4 h-4 rounded-full animate-bounce delay-100" style={{ backgroundColor: '#ff6a24' }}></div>
        <div className="w-4 h-4 rounded-full animate-bounce delay-200" style={{ backgroundColor: '#1c58f4' }}></div>
        <div className="w-4 h-4 rounded-full animate-bounce delay-300" style={{ backgroundColor: '#00b76b' }}></div>
    </div>
);

// Unit conversion functions
const convertLbsToKg = (lbs) => {
    return lbs * 0.453592;
};

const convertInchesToCm = (inches) => {
    return inches * 2.54;
};

const getWeightInKg = (weight) => {
    if (!weight || !weight.value) return 0;
    const value = parseFloat(weight.value);
    return weight.unit === 'lbs' ? convertLbsToKg(value) : value;
};

const getDimensionInCm = (dimension) => {
    if (!dimension || !dimension.value) return 0;
    const value = parseFloat(dimension.value);
    return dimension.unit === 'in' ? convertInchesToCm(value) : value;
};

const getCountryCodeByName = (countryInput) => {
    if (!countryInput) return null;
    if (Array.isArray(countryInput)) {
        if (countryInput.length === 0) return null;
        return getAlpha2Code(countryInput[0]);
    }
    if (typeof countryInput === 'string') {
        return getAlpha2Code(countryInput);
    }
    return null;
};

const needsStateSelection = (country) => {
    const countryCode = getCountryCodeByName(country);
    return countryCode === 'CA' || countryCode === 'BR';
};

// --- API Base URLs loaded from .env ---
const ONESCAN_API_BASE_URL = process.env.REACT_APP_ONESCAN_API_BASE_URL;
const COMPLIANCE_API_BASE_URL = process.env.REACT_APP_COMPLIANCE_API_BASE_URL;
const HSCODE_API_BASE_URL = process.env.REACT_APP_HSCODE_API_BASE_URL;
const API_LOGIN_USERNAME = process.env.REACT_APP_API_LOGIN_USERNAME;
// const DHL_CLIENT_ID = process.env.REACT_APP_DHL_CLIENT_ID;
// const DHL_CLIENT_SECRET = process.env.REACT_APP_DHL_CLIENT_SECRET;
const DESCARTES_CLIENT_ID = process.env.REACT_APP_DESCARTES_CLIENT_ID;
const DESCARTES_CLIENT_SECRET = process.env.REACT_APP_DESCARTES_CLIENT_SECRET;
// --------------------------------------

const IorCalculatorForm = () => {
    const { user } = useMyContext();
    const { currencies } = useContext(CurrencyContext);
    const [errors, setErrors] = useState({});
    const [generalCompliance, setGeneralCompliance] = useState([]);
    const [readOnly, setReadOnly] = useState(false)
    const [specificCompliance, setSpecificCompliance] = useState([]);
    const [hasEstimated, setHasEstimated] = useState(false);

    // Estimate-related state
    const [loading, setLoading] = useState(false);
    const [adminEstimate, setAdminEstimate] = useState(null);
    const [dhlEstimation, setDhlEstimation] = useState(null);
    // const [tokenExpiry, setTokenExpiry] = useState(null);
    // const [accessToken, setAccessToken] = useState(null);
    // const [pricingStrategy, setPricingStrategy] = useState('EXACT');
    const [totalItemsValue, setTotalItemsValue] = useState(0);
    const [itemsTotalQty, setItemsTotalQty] = useState(0);
    const [totalDimensionsWeight, setTotalDimensionsWeight] = useState(0);
    const [totalDimensionsLength, setTotalDimensionsLength] = useState(0);
    const [totalDimensionsWidth, setTotalDimensionsWidth] = useState(0);
    const [totalDimensionsHeight, setTotalDimensionsHeight] = useState(0);
    const [totalItemsQuantity, setTotalItemsQuantity] = useState(0);
    const [insurance, setInsurance] = useState(0);
    const [airfreight, setAirfreight] = useState(0);
    const [cif, setCif] = useState(0);
    const [duty, setDuty] = useState(0);
    const [collectionValue, setCollectionValue] = useState(0);
    const [calculatedValues, setCalculatedValues] = useState({});
    const [estimateSummary, setEstimateSummary] = useState(null);
    const [warningPopup, setWarningPopup] = useState({ isOpen: false, message: "" });
    const [disabledCountries, setDisabledCountries] = useState([]);
    const [disabledDutyVatCountries, setDisabledDutyVatCountries] = useState([]);
    const [eccnCodes, setEccnCodes] = useState([]);
    const [leadTimeData, setLeadTimeData] = useState(null);
    const [showRecalcPopup, setShowRecalcPopup] = useState(false);
    const [savedEstimateId, setSavedEstimateId] = useState(null);

    // NEW STATE for HS Code validation data
    const [hsCodeData, setHsCodeData] = useState({});

    const [formData, setFormData] = useState({
        userId: user.uid,
        userName: user.name,
        userCity: user.city,
        userCountry: user.country,
        postalCode: user.postalCode,
        insuranceSelected: "NO",
        email: user.email,
        currency: user.currency || "USD", // Use user currency
        itemsCurrency: user.currency || "USD", // Use user currency
        selectedService: "IOR",
        hsCode: "",
        dimensions: [
            {
                weight: { value: "", unit: "kg" },
                length: { value: "", unit: "cm" },
                width: { value: "", unit: "cm" },
                height: { value: "", unit: "cm" },
                quantity: { value: '' }
            }
        ],
        originCountry: "",
        originState: "",
        destinationCountry: "",
        destinationState: "",
        destinationCity: "",
        originCity: "",
        status: false,
        pending: "",
        requiredDoc: "not_Required",
        handling: "NO",
        allItemsNew: true,
        items: [
            {
                itemName: "",
                itemHSCode: "",
                itemValue: "",
                countryOrigin: "",
                quantity: "",
                hasHSCode: true,
                itemNew: true,
                hsCodeSource: "database",
                itemEccnCode: ""
            },
        ],
        eccnCode: null,
        quoteNumber: "",
        tradeOSEstimate: true
    });

    // Persist across in-app tab switches only (clears on full reload)
    const { saved, save } = useScopedFormState("client-ior");

    // Load saved form on mount
    useEffect(() => {
        if (saved) {
            if (saved.formData) setFormData(saved.formData);
            if (saved.hsCodeData) setHsCodeData(saved.hsCodeData);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Keep store in sync
    useEffect(() => {
        save({ formData, hsCodeData });
    }, [formData, hsCodeData, save]);

    // Derived flag: any HS validation still running?
    const hsValidationPending = useMemo(() => {
        try {
            return Object.values(hsCodeData || {}).some(v => v && v.status === 'loading');
        } catch (e) {
            return false;
        }
    }, [hsCodeData]);

    // --- JWT Token Fetch Function for HSCode API ---
    const fetchJwtToken = useCallback(async () => {
        if (!HSCODE_API_BASE_URL || !API_LOGIN_USERNAME) {
            toast.error("HS Code API configuration missing.");
            return null;
        }

        const loginUrl = `${HSCODE_API_BASE_URL}/api/login`;
        try {
            const res = await fetch(loginUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    username: API_LOGIN_USERNAME
                }),
            });

            if (!res.ok) {
                throw new Error(`Login failed with status: ${res.status}`);
            }

            const data = await res.json();
            if (data.token) {
                return data.token;
            } else {
                throw new Error("Token not found in login response.");
            }
        } catch (error) {
            console.error("Error fetching JWT token:", error);
            toast.error("Authentication failed for HS Code lookup.");
            return null;
        }
    }, []);

    // --- JWT Token Fetch Function for OneScan ---
    const getOneScanToken = useCallback(async () => {
        if (!ONESCAN_API_BASE_URL || !API_LOGIN_USERNAME) {
            toast.error("OneScan API configuration missing.");
            return null;
        }

        try {
            const response = await fetch(`${ONESCAN_API_BASE_URL}/api/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    username: API_LOGIN_USERNAME
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to get OneScan access token');
            }

            const data = await response.json();
            return data.token;
        } catch (error) {
            toast.error('Failed to authenticate with OneScan: ' + error.message);
            console.error('OneScan Token Error:', error);
            return null;
        }
    }, []);

    // --- JWT Token Fetch Function for Compliance API ---
    const getComplianceToken = useCallback(async () => {
        if (!COMPLIANCE_API_BASE_URL || !API_LOGIN_USERNAME) {
            toast.error("Compliance API configuration missing.");
            return null;
        }

        try {
            const response = await fetch(`${COMPLIANCE_API_BASE_URL}/api/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    username: API_LOGIN_USERNAME
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to get Compliance access token');
            }

            const data = await response.json();
            return data.token;
        } catch (error) {
            toast.error('Failed to authenticate with Compliance API: ' + error.message);
            console.error('Compliance Token Error:', error);
            return null;
        }
    }, []);

    // --- Centralized HS Code Check Function (FIXED) ---
    const handleCheckHsCode = useCallback(async (code, country, itemIndex) => {
        const fullCode = String(code);
        if (!fullCode || fullCode.length < 5 || !country) {
            setHsCodeData(prev => ({
                ...prev,
                [itemIndex]: {
                    status: "incomplete",
                    results: [],
                    hsCode: fullCode,
                    itemIndex: itemIndex
                }
            }));
            return;
        }

        setHsCodeData(prev => ({
            ...prev,
            [itemIndex]: {
                status: "loading",
                results: [],
                hsCode: fullCode,
                itemIndex: itemIndex
            }
        }));

        const token = await fetchJwtToken();
        if (!token) {
            setHsCodeData(prev => ({
                ...prev,
                [itemIndex]: {
                    status: "invalid",
                    results: [],
                    hsCode: fullCode,
                    itemIndex: itemIndex
                }
            }));
            return;
        }

        const codeForApi = fullCode.substring(0, 5);

        try {
            const res = await fetch(`${HSCODE_API_BASE_URL}/hscodes`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    country: getCountryCodeByName(country),
                    hs: codeForApi
                }),
            });

            if (!res.ok) {
                setHsCodeData(prev => ({
                    ...prev,
                    [itemIndex]: {
                        status: "invalid",
                        results: [],
                        hsCode: fullCode,
                        itemIndex: itemIndex
                    }
                }));
                return;
            }

            const data = await res.json();

            if (data.status === "error") {
                toast.error(data.message || "An error occurred during HS Code lookup.");
                setHsCodeData(prev => ({
                    ...prev,
                    [itemIndex]: {
                        status: "invalid",
                        results: [],
                        hsCode: fullCode,
                        itemIndex: itemIndex
                    }
                }));
                return;
            }

            let effectiveStatus = data.status;
            const results = Array.isArray(data.results) ? data.results : [];
            const hasExactMatch = fullCode.length > 5 && results.some(r => String(r.code) === String(fullCode));

            if (hasExactMatch) {
                effectiveStatus = "valid";
            } else if (fullCode.length > 5) {
                effectiveStatus = results.length > 0 ? "partially_correct" : "invalid";
            } else {
                effectiveStatus = data.status;
            }

            // CRITICAL: Store results with proper item isolation
            setHsCodeData(prev => ({
                ...prev,
                [itemIndex]: {
                    status: effectiveStatus,
                    results: data.results || [],
                    hsCode: fullCode,
                    itemIndex: itemIndex,
                    message: data.message || ""
                }
            }));

        } catch (error) {
            console.error("HS Code lookup error:", error);
            toast.error("Something went wrong with HS Code lookup.");
            setHsCodeData(prev => ({
                ...prev,
                [itemIndex]: {
                    status: "invalid",
                    results: [],
                    hsCode: fullCode,
                    itemIndex: itemIndex
                }
            }));
        }
    }, [fetchJwtToken, getCountryCodeByName, HSCODE_API_BASE_URL]);

    const handleOneScanUpload = async (e) => {
        if (!formData.originCountry || !formData.destinationCountry) {
            toast.warning("Please select Origin and Destination Country before uploading.");
            e.target.value = null;
            return;
        }

        const file = e.target.files[0];
        if (!file) return;

        setReadOnly(true);

        // Show persistent toast that won't auto-dismiss
        const toastId = toast.info("Processing invoice...", {
            autoClose: false,
            closeButton: false
        });

        try {
            const token = await getOneScanToken();
            if (!token) {
                toast.dismiss(toastId);
                return;
            }

            const payload = new FormData();
            payload.append("file", file);

            const uploadUrl = `${ONESCAN_API_BASE_URL}/upload`;

            const res = await fetch(uploadUrl, {
                method: "POST",
                headers: {
                    "Accept": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: payload
            });

            if (!res.ok) throw new Error("Failed to upload file to OneScan");
            const data = await res.json();

            const itemsFromApi = data?.items || [];

            if (itemsFromApi.length === 0) {
                toast.dismiss(toastId);
                toast.warning("No items found in the invoice");
                return;
            }

            const itemData = itemsFromApi.map((i) => ({
                itemName: i.item_name || "",
                itemHSCode: i.hs_code || "",
                quantity: i.quantity || "",
                itemValue: i.unit_price || "",
                countryOrigin: i.country_of_origin || "",
                hasHSCode: !!i.hs_code,
                itemNew: true,
                hsCodeSource: !!i.hs_code ? "database" : "",
                itemEccnCode: ""
            }));

            setFormData((prev) => ({
                ...prev,
                items: itemData,
                allItemsNew: true,
                itemsCurrency: data?.currency || prev.itemsCurrency || "USD"
            }));

            // Dismiss the processing toast and show success
            toast.dismiss(toastId);
            toast.success(`Invoice Data Extracted Successfully - ${itemData.length} items found`);

            const currentDestinationCountry = data.destinationCountry || formData.destinationCountry;

            setHsCodeData({});

            for (let i = 0; i < itemData.length; i++) {
                const item = itemData[i];
                if (item.itemHSCode && currentDestinationCountry) {
                    await handleCheckHsCode(item.itemHSCode, currentDestinationCountry, i);
                }
            }

        } catch (error) {
            console.error("Error uploading file:", error);
            toast.dismiss(toastId);
            toast.error("Error uploading file: " + error.message);
        } finally {
            setReadOnly(false);
            e.target.value = null;
        }
    };

    const uploadButton = () => {
        return (
            <div data-tour-id="onescan-button" className={`${readOnly && "cursor-not-allowed opacity-50"} shine w-full md:w-fit whitespace-nowrap bg-gradient-to-r from-orange-600 to-orange-400 text-white shadow-md shadow-orange-300 hover:shadow-xl hover:shadow-orange-300 transition-all duration-300 ease-in-out rounded-lg font-medium`}>
                <label
                    htmlFor="file"
                    className={`block w-full h-full px-5 py-2 ${readOnly ? "cursor-not-allowed" : "cursor-pointer"}`}
                >
                    <input
                        onChange={handleOneScanUpload}
                        type="file"
                        name="file"
                        id="file"
                        className="hidden"
                        accept=".pdf, .doc, .docx, .xls, .xlsx"
                        disabled={readOnly}
                    />
                    {readOnly ? "Uploading..." : "OneScan™"}
                </label>
            </div>
        )
    }

    // In your handleSubmit or fetchLeadTimes function
    const fetchLeadTimes = async (countryName) => {
        try {
            const response = await api.get(`${API_BASE_URL}/lead-times`);
            const allLeadTimes = response.data || [];

            // Find matching country (case-insensitive)
            const match = allLeadTimes.find(
                lt => lt.countryName.toLowerCase() === countryName.toLowerCase()
            );

            setLeadTimeData(match || null);
        } catch (error) {
            console.error("Error fetching lead times:", error);
            setLeadTimeData(null);
        }
    };

    const handleInputChange = useCallback((e) => {
        const { name, value } = e.target || e;
        setFormData((prevData) => {
            let newValue = value;
            if (name === "selectedService") {
                if (value === "IOR & EOR" || value === "IOR&EOR") {
                    newValue = "EOR & IOR";
                }

                let eccnReset = {};
                if (newValue !== "EOR" && newValue !== "EOR & IOR") {
                    eccnReset.eccnCode = null;
                }
                if (newValue === "EOR") {
                    eccnReset.requiredDoc = "not_Required";
                }

                return {
                    ...prevData,
                    [name]: newValue,
                    ...eccnReset,
                    items: prevData.items.map(item => ({
                        ...item,
                        itemEccnCode: (newValue === "EOR" || newValue === "EOR & IOR") ? item.itemEccnCode : ""
                    }))
                };
            }

            const newData = { ...prevData, [name]: newValue };
            if (name === "originCountry" && !needsStateSelection(value)) {
                newData.originState = "";
            }

            if (name === "destinationCountry") {
                if (!needsStateSelection(value)) {
                    newData.destinationState = "";
                }
                newData.items = prevData.items.map(item => ({
                    ...item,
                    itemHSCode: "",
                }));
                setHsCodeData({});
            }

            return newData;
        });

        setErrors((prevErrors) => ({ ...prevErrors, [name]: false }));
    }, [setFormData, setErrors, needsStateSelection]);

    // Fetch Disabled Countries & ECCN Codes
    useEffect(() => {
        const fetchDisabledCountries = async () => {
            try {
                const response = await api.get(`${API_BASE_URL}/countries/disabled`);
                setDisabledCountries(response.data);
            } catch (error) {
                console.error('Error fetching disabled countries:', error);
            }
        };
        const fetchDisabledDutyVatCountries = async () => {
            try {
                const response = await api.get(`${API_BASE_URL}/duty-vat/disabled`);
                setDisabledDutyVatCountries(response.data);
            } catch (error) {
                console.error('Error fetching disabled duty & VAT countries:', error);
            }
        };
        const fetchEccnCodes = async () => {
            try {
                const response = await api.get(`${API_BASE_URL}/eccn`);
                setEccnCodes(response.data);
            } catch (error) {
                console.error("Error fetching ECCN codes:", error);
            }
        };

        fetchDisabledCountries();
        fetchDisabledDutyVatCountries();
        fetchEccnCodes();
    }, []);

    // Total Items Value and Quantity Calculation
    useEffect(() => {
        const calculateTotalValue = () => {
            const customerCurrency = user.currency || "USD"; // Use logged-in user's currency
            const itemsCurrency = formData.itemsCurrency || "USD";

            const totalInItemsCurrency = formData.items.reduce((sum, item) => {
                const value = parseFloat(item.itemValue) || 0;
                const quantity = parseFloat(item.quantity) || 0;
                return sum + (value * quantity);
            }, 0);

            const totalQuantity = formData.items.reduce((sum, item) => {
                const quantity = parseFloat(item.quantity) || 0;
                return sum + quantity;
            }, 0);

            if (customerCurrency === itemsCurrency) {
                setTotalItemsValue(totalInItemsCurrency);
                setItemsTotalQty(totalQuantity);
                return;
            }

            let conversionRate = 1;
            const itemsCurrencyData = currencies.find(curr => curr.name === itemsCurrency);

            if (itemsCurrencyData && itemsCurrencyData.exchangeRates && itemsCurrencyData.exchangeRates[customerCurrency]) {
                conversionRate = itemsCurrencyData.exchangeRates[customerCurrency];
            } else {
                const customerCurrencyData = currencies.find(curr => curr.name === customerCurrency);
                if (customerCurrencyData && customerCurrencyData.exchangeRates && customerCurrencyData.exchangeRates[itemsCurrency]) {
                    conversionRate = 1 / customerCurrencyData.exchangeRates[itemsCurrency];
                } else {
                    const itemsToUSD = itemsCurrencyData?.exchangeRates?.USD || 1;
                    const USDToCustomerCurrency = customerCurrencyData?.exchangeRates?.USD ? (1 / customerCurrencyData.exchangeRates.USD) : 1;
                    conversionRate = itemsToUSD * USDToCustomerCurrency;
                }
            }
            const totalInCustomerCurrency = totalInItemsCurrency * conversionRate;

            setTotalItemsValue(totalInCustomerCurrency);
            setItemsTotalQty(totalQuantity);
        };

        calculateTotalValue();
    }, [formData.items, user.currency, formData.itemsCurrency, currencies]); // Depend on user.currency

    // Total Dimensions Calculation
    useEffect(() => {
        const calculateTotalDimensions = () => {
            const totalWeight = formData.dimensions.reduce((sum, dim) => {
                const weightInKg = getWeightInKg(dim.weight);
                const quantity = parseFloat(dim.quantity.value || 0);
                return sum + (weightInKg * quantity);
            }, 0);

            const totalLength = formData.dimensions.reduce((sum, dim) => {
                return sum + getDimensionInCm(dim.length);
            }, 0);

            const totalWidth = formData.dimensions.reduce((sum, dim) => {
                return sum + getDimensionInCm(dim.width);
            }, 0);

            const totalHeight = formData.dimensions.reduce((sum, dim) => {
                return sum + getDimensionInCm(dim.height);
            }, 0);

            const totalQuantity = formData.dimensions.reduce((sum, dim) => {
                return sum + (parseFloat(dim.quantity.value) || 0);
            }, 0);

            setTotalDimensionsWeight(totalWeight);
            setTotalDimensionsLength(totalLength);
            setTotalDimensionsWidth(totalWidth);
            setTotalDimensionsHeight(totalHeight);
            setTotalItemsQuantity(totalQuantity);
        };

        calculateTotalDimensions();
    }, [formData.dimensions]);

    // Insurance Calculation
    useEffect(() => {
        const calculateInsurance = () => {
            const calculatedInsurance = formData.insuranceSelected === 'YES' ? totalItemsValue * 0.0075 : 0;
            setInsurance(calculatedInsurance);
        };

        calculateInsurance();
    }, [formData.insuranceSelected, totalItemsValue]);

    // Airfreight Calculation
    useEffect(() => {
        const calculateAirfreightCost = () => {
            const totalVolumetricWeight = formData.dimensions.reduce((sum, dim) => {
                const lengthCm = getDimensionInCm(dim.length);
                const widthCm = getDimensionInCm(dim.width);
                const heightCm = getDimensionInCm(dim.height);
                const quantity = parseFloat(dim.quantity.value || 0);

                let volWeight = (lengthCm * widthCm * heightCm) / 6000;
                return sum + (volWeight * quantity);
            }, 0);

            const chargeableWeight = Math.max(totalVolumetricWeight, totalDimensionsWeight);

            let airfreightCost;
            if (chargeableWeight < 360) {
                airfreightCost = 6.50 * chargeableWeight + 250;
            } else if (chargeableWeight >= 360 && chargeableWeight <= 1080) {
                airfreightCost = 5.00 * chargeableWeight + 250;
            } else {
                airfreightCost = 4.00 * chargeableWeight + 250;
            }

            setAirfreight(airfreightCost);
        };

        calculateAirfreightCost();
    }, [formData.dimensions, totalDimensionsWeight]);

    // Fetch Admin Estimate
    const fetchAdminEstimate = useCallback(async (
        loadCountry,
        unloadCountry,
        selectedTemplate,
        airfreightCurrency
    ) => {
        if (!loadCountry || !unloadCountry || !selectedTemplate || !airfreightCurrency) {
            return;
        }

        const standardizedWeightUnit = "kg";

        try {
            const response = await api.get(`${API_BASE_URL}/estimates/estimate-lookup`, {
                params: {
                    loadCountry,
                    unloadCountry,
                    selectedTemplate,
                    airfreightCurrency,
                    afcWeightUnit: standardizedWeightUnit,
                },
                timeout: 15000,
            });

            const potentialMatches = response.data;

            if (potentialMatches && potentialMatches.length > 0) {
                const exactMatch = potentialMatches.find((estimate) => {
                    const loadCountryMatch = Array.isArray(estimate.loadCountry)
                        ? estimate.loadCountry.includes(loadCountry)
                        : estimate.loadCountry === loadCountry;

                    return (
                        loadCountryMatch &&
                        estimate.unloadCountry === unloadCountry &&
                        estimate.selectedTemplate?.name === selectedTemplate &&
                        estimate.afcWeightUnit === standardizedWeightUnit &&
                        estimate.airfreightCurrency === airfreightCurrency
                    );
                });

                if (exactMatch) {
                    setAdminEstimate(exactMatch);
                    const pendingServices =
                        exactMatch.selectedTemplate?.services
                            ?.filter((service) => service.values?.includes("Pending"))
                            ?.map((service) => service.description) || [];

                    setFormData((prevData) => ({
                        ...prevData,
                        airfreightCurrency: exactMatch.airfreightCurrency,
                        afcWeightUnit: exactMatch.afcWeightUnit,
                        selectedTemplate: exactMatch.selectedTemplate,
                        pending: pendingServices.join(", "),
                    }));
                    return exactMatch; // Return the match
                } else {
                    setAdminEstimate(null);
                    return null; // Return null
                }
            } else {
                setAdminEstimate(null);
                return null; // Return null
            }
        } catch (error) {
            if (error.response?.status !== 404) {
                toast.error("Could not load estimation rules. Please try again.");
            }
            setAdminEstimate(null);
            return null; // Return null on error
        }
    }, []);

    useEffect(() => {
        const debounceTimeout = setTimeout(() => {
            if (
                formData.originCountry &&
                formData.destinationCountry &&
                formData.selectedService &&
                formData.itemsCurrency &&
                !loading
            ) {
                fetchAdminEstimate(
                    formData.originCountry,
                    formData.destinationCountry,
                    formData.selectedService,
                    formData.itemsCurrency
                );
            }
        }, 500);

        return () => clearTimeout(debounceTimeout);
    }, [
        formData.originCountry,
        formData.destinationCountry,
        formData.selectedService,
        formData.itemsCurrency,
        loading,
        fetchAdminEstimate
    ]);

    /*
    // DHL Token Management
    const getDHLToken = useCallback(async () => {
        if (!DHL_CLIENT_ID || !DHL_CLIENT_SECRET) {
            toast.error("DHL API configuration missing.");
            return null;
        }

        try {
            const response = await fetch('https://api.dhl.com/auth/v1/token', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: new URLSearchParams({
                    grant_type: 'client_credentials',
                    response_type: 'access_token',
                    client_id: DHL_CLIENT_ID,
                    client_secret: DHL_CLIENT_SECRET
                })
            });

            if (!response.ok) {
                throw new Error('Failed to get access token');
            }

            const data = await response.json();
            setAccessToken(data.access_token);
            setTokenExpiry(Date.now() + (15 * 60 * 1000));
            return data.access_token;
        } catch (error) {
            toast.error('Failed to authenticate with DHL');
            return null;
        }
    }, []);

    const ensureValidToken = useCallback(async () => {
        if (!accessToken || !tokenExpiry || Date.now() >= tokenExpiry) {
            return await getDHLToken();
        }
        return accessToken;
    }, [accessToken, tokenExpiry, getDHLToken]);

    // DHL Request Body Preparation
    const prepareDHLRequestBody = useCallback((formItems) => {
        const senderCountry = getCountryCodeByName(formData.originCountry) || '';
        const receiverCountry = getCountryCodeByName(formData.destinationCountry) || '';
        const customerCurrency = user.currency || "USD"; // Use user currency
        const itemsCurrency = formData.itemsCurrency || "USD";
        const dhlCurrency = customerCurrency === "USD" ? "USD" : "EUR";

        const convertToDHLCurrency = (amount, fromCurrency) => {
            if (fromCurrency === dhlCurrency) return amount;
            let conversionRate = 1;
            const fromCurrencyData = currencies.find(curr => curr.name === fromCurrency);

            if (fromCurrencyData && fromCurrencyData.exchangeRates && fromCurrencyData.exchangeRates[dhlCurrency]) {
                conversionRate = fromCurrencyData.exchangeRates[dhlCurrency];
            } else {
                const toCurrencyData = currencies.find(curr => curr.name === dhlCurrency);
                if (toCurrencyData && toCurrencyData.exchangeRates && toCurrencyData.exchangeRates[fromCurrency]) {
                    conversionRate = 1 / toCurrencyData.exchangeRates[fromCurrency];
                }
            }
            return amount * conversionRate;
        };

        const getRegionCode = (country, state) => {
            if (needsStateSelection(country)) return state || '';
            return state || '';
        };

        const senderState = getRegionCode(formData.originCountry, formData.originState);
        const receiverState = getRegionCode(formData.destinationCountry, formData.destinationState);

        const requestBody = {
            pricingStrategy: pricingStrategy,
            senderAddress: {
                country: senderCountry,
                state: senderState,
            },
            consigneeAddress: {
                country: receiverCountry,
                state: receiverState,
            },
            packageDetails: {
                outputCurrency: dhlCurrency,
                transportMode: "AIR",
                endUse: "BUSINESS",
                clearanceMode: "COURIER",
                tax: {
                    included: false,
                    percent: 0,
                    return: true
                },
                freightCharge: {
                    value: 0,
                    currency: dhlCurrency
                },
                insuranceCharge: {
                    value: 0,
                    currency: dhlCurrency
                }
            },
            customsDetails: formItems.map(item => {
                const itemValueInItemsCurrency = parseFloat(item.itemValue) || 0;
                const itemValueInDHLCurrency = convertToDHLCurrency(itemValueInItemsCurrency * 1.1, itemsCurrency);

                return {
                    itemId: `ITEM_${Math.random().toString(36).substr(2, 9)}`,
                    itemDescription: item.itemName,
                    hsCode: String(item.itemHSCode || ''),
                    countryOfOrigin: getAlpha2Code(item.countryOrigin),
                    itemQuantity: {
                        value: parseInt(item.quantity) || 0,
                        unit: "PCS"
                    },
                    itemValue: {
                        value: itemValueInDHLCurrency,
                        currency: dhlCurrency
                    },
                    weight: {
                        value: totalDimensionsWeight,
                        unit: "KG"
                    },
                };
            })
        };

        return requestBody;
    }, [formData, pricingStrategy, user.currency, currencies, totalDimensionsWeight]); // Use user.currency

    // DHL Estimation Call
    const getDHLEstimation = useCallback(async (items) => {
        const token = await ensureValidToken();
        if (!token) return null;

        const filterDHLResponse = (dhlResponse) => {
            if (!dhlResponse) return null;
            const filteredFeeTotals = dhlResponse.feeTotals.filter(fee => fee.value > 0);
            return {
                quoteId: dhlResponse.quoteId,
                pricingStrategy: dhlResponse.pricingStrategy,
                feeTotals: filteredFeeTotals,
                consigneeAddress: dhlResponse.consigneeAddress,
                senderAddress: dhlResponse.senderAddress,
                packageDetails: {
                    outputCurrency: dhlResponse.packageDetails.outputCurrency,
                    clearanceMode: dhlResponse.packageDetails.clearanceMode,
                    transportMode: dhlResponse.packageDetails.transportMode,
                    endUse: dhlResponse.packageDetails.endUse,
                    freightCharge: dhlResponse.packageDetails.freightCharge,
                    insuranceCharge: dhlResponse.packageDetails.insuranceCharge,
                }
            };
        };

        try {
            const requestBody = prepareDHLRequestBody(items);

            const callApi = async (body) => {
                const response = await fetch('https://api.dhl.com/dtc/v1/quotes', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify(body)
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.type?.includes('400.0904002') ? '400.0904002' : errorData.message);
                }
                return response.json();
            };

            let data;
            try {
                data = await callApi(requestBody);
            } catch (error) {
                if (error.message === '400.0904002') {
                    setPricingStrategy('MAXIMUM');
                    const retryBody = { ...requestBody, pricingStrategy: 'MAXIMUM' };
                    data = await callApi(retryBody);
                } else {
                    throw error;
                }
            }

            const filteredData = filterDHLResponse(data);
            const dutyValue = data.feeTotals.find(fee => fee.name === 'DUTY')?.value || 0;
            setDuty(dutyValue);
            setDhlEstimation(filteredData);
            return { dhlData: filteredData, dutyValue };
        } catch (error) {
            return null;
        }
    }, [ensureValidToken, prepareDHLRequestBody]);
    */
    /*
        // DHL Token Management (Original)
        // ...
        */
    /*
    const getDHLToken = useCallback(async () => { ... }, []);
    const ensureValidToken = useCallback(async () => { ... }, [accessToken, tokenExpiry, getDHLToken]);
    const prepareDHLRequestBody = useCallback((formItems) => { ... }, [formData, pricingStrategy, currencies, totalDimensionsWeight]);
    const getDHLEstimation = useCallback(async (items) => { ... }, [ensureValidToken, prepareDHLRequestBody]);
    */

    // --- Descartes API Integration ---

    // Descartes Token Management
    const getDescartesToken = useCallback(async () => {
        try {
            const taxApiBaseUrl = process.env.REACT_APP_TAX_API_BASE_URL || "http://localhost:8080";
            const response = await fetch(`${taxApiBaseUrl}/api/ous-taxes-api/token`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "x-ous-access-key": process.env.REACT_APP_TAX_API_ACCESS_KEY
                },
                body: JSON.stringify({})
            });

            if (!response.ok) {
                if (response.status === 429) {
                    const errorData = await response.json().catch(() => ({}));
                    const msg = errorData.error || "Rate limit exceeded. Please try again later.";
                    toast.error(msg); // Notify user
                    return null;
                }
                throw new Error("Failed to get Descartes access token");
            }

            const data = await response.json();
            return data.data?.accessToken;
        } catch (error) {
            console.error("Descartes Token Error:", error);
            // toast.error("Failed to authenticate with Descartes"); // Avoid double toast if 429 handled
            return null;
        }
    }, []);

    // Descartes Estimation Call
    const getDescartesEstimation = useCallback(async (items, adminEstimate) => {
        const token = await getDescartesToken();
        if (!token) return null;

        const taxMap = new Map();
        let processedAny = false;

        // Recalculate total items value locally to ensure ratio is accurate
        const localTotalItemsValue = items.reduce((sum, item) => {
            return sum + ((parseFloat(item.itemValue) || 0) * (parseFloat(item.quantity) || 0));
        }, 0);

        // Check if we need to calculate Collection and Airfreight costs locally
        let collectionCostForEstimate = 0;
        let airfreightCostForEstimate = 0;
        let airfreightServiceFound = false;

        if (adminEstimate && adminEstimate.selectedTemplate && adminEstimate.selectedTemplate.services) {
            // 1. Calculate Collection
            const collectionService = adminEstimate.selectedTemplate.services.find(
                service => service.description.toUpperCase() === 'COLLECTION'
            );
            if (collectionService) {
                collectionCostForEstimate = calculateServiceValue(
                    collectionService,
                    formData,
                    {},
                    localTotalItemsValue, // Use locally calculated value
                    totalDimensionsWeight,
                    totalDimensionsLength,
                    totalDimensionsWidth,
                    totalDimensionsHeight,
                    0,
                    insurance,
                    totalItemsQuantity,
                    itemsTotalQty
                );
            }

            // 2. Calculate Airfreight
            const airfreightService = adminEstimate.selectedTemplate.services.find(
                service => service.description.toUpperCase().includes('AIR FREIGHT EST')
            );

            if (airfreightService) {
                airfreightServiceFound = true;
                airfreightCostForEstimate = calculateServiceValue(
                    airfreightService,
                    formData,
                    {},
                    localTotalItemsValue, // Use locally calculated value
                    totalDimensionsWeight,
                    totalDimensionsLength,
                    totalDimensionsWidth,
                    totalDimensionsHeight,
                    0,
                    insurance,
                    totalItemsQuantity,
                    itemsTotalQty
                );
            }
        }

        // Use calculated values if available, otherwise fallback to state (safeguard)
        // Note: For Client calculator, state logic is delayed by useEffect, so local calculation is critical.
        const totalAirfreight = airfreightServiceFound ? (parseFloat(airfreightCostForEstimate) || 0) : (parseFloat(airfreight) || 0);
        const totalCollection = (collectionCostForEstimate > 0) ? collectionCostForEstimate : (parseFloat(collectionValue) || 0);
        const totalInsurance = parseFloat(insurance) || 0;
        const totalExtras = totalAirfreight + totalInsurance + totalCollection;

        // console.log("Descartes Client: Cost Debug:", { localTotalItemsValue, totalExtras, totalAirfreight, totalInsurance, totalCollection, itemsCount: items.length });

        for (const item of items) {
            // Calculate total value for this line item in original currency
            const itemValue = parseFloat(item.itemValue) || 0;
            const itemQty = parseFloat(item.quantity) || 0;
            const itemTotalValue = itemValue * itemQty;

            // Distribute extras proportionally based on item value
            const ratio = localTotalItemsValue > 0 ? (itemTotalValue / localTotalItemsValue) : 0;
            const allocatedExtra = totalExtras * ratio;
            const finalCostOfGoods = itemTotalValue + allocatedExtra;

            const payload = {
                "customerId": "1024",
                "clientReferenceId": `LC-${Math.floor(Math.random() * 100000)}`,
                "tariffNo": item.itemHSCode,
                "importCountryCode": getAlpha2Code(formData.destinationCountry),
                "exportCountryCode": getAlpha2Code(formData.originCountry),
                "modeOfTransport": "AIR",
                "currencyCode": formData.currency || "USD",
                "costOfGoods": finalCostOfGoods.toFixed(2),
                "shippingCost": "0.0",
                "insuranceCost": "0.0",
                "otherCost": "0.0",
                "uom1Code": "1",
                "uom1Quantity": (item.quantity || 1).toString()
            };

            try {
                // console.log(`Descartes: Requesting estimation for item: ${item.itemName}`, payload);
                const taxApiBaseUrl = process.env.REACT_APP_TAX_API_BASE_URL || "http://localhost:8080";
                const res = await fetch(`${taxApiBaseUrl}/api/ous-taxes-api/estimate`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "X-DSG-CONTENT_authorization": token,
                        "Authorization": token, // Standard header as fallback
                        "x-ous-access-key": process.env.REACT_APP_TAX_API_ACCESS_KEY
                    },
                    body: JSON.stringify(payload)
                });

                // console.log(`Descartes: Estimation Response Status for ${item.itemName}:`, res.status);

                if (!res.ok) {
                    if (res.status === 429) {
                        const errorData = await res.json().catch(() => ({}));
                        const msg = errorData.error || "Rate limit reached. partial calculations shown.";
                        toast.error(msg);
                        break; // Stop requests
                    }
                    const errBody = await res.text();
                    console.error(`Descartes API Error for item ${item.itemName}: ${res.status}`, errBody);
                    continue;
                }

                const data = await res.json();
                // console.log(`Descartes: Estimation data received for ${item.itemName}`, data);

                if (data.data && data.data.breakupDetails) {
                    processedAny = true;
                    data.data.breakupDetails.forEach(detail => {
                        // CRITICAL FIX: Only process entries with isDefault: "1" to exclude exemptions
                        if (detail.isDefault !== "1") {
                            return; // Skip optional charges and exemptions
                        }

                        if (["DUTY", "TAX", "VAT"].includes(detail.costType)) {
                            const amount = parseFloat(detail.amount) || 0;

                            // Skip negative amounts (safety check - shouldn't happen with isDefault=1)
                            if (amount < 0) {
                                return;
                            }

                            let name;

                            // Combine all DUTY types into one "DUTY" entry
                            if (detail.costType === "DUTY") {
                                name = "DUTY";
                            }
                            // Keep VAT separate - check both costType and groupName
                            else if (detail.costType === "VAT" ||
                                (detail.groupName && detail.groupName.toLowerCase().includes("vat"))) {
                                name = "VAT";
                            }
                            // Combine all other taxes into "Taxes"
                            else if (detail.costType === "TAX") {
                                name = "Taxes";
                            }

                            const current = taxMap.get(name) || 0;
                            taxMap.set(name, current + amount);
                        }
                    });
                }
            } catch (error) {
                console.error("Descartes Estimation Error for item:", item.itemName, error);
            }
        }

        if (!processedAny) {
            return null;
        }

        const feeTotals = [];
        taxMap.forEach((value, key) => {
            if (value > 0) {
                feeTotals.push({ name: key, value: value });
            }
        });

        const dutyValue = taxMap.get("DUTY") || 0;

        const descartesData = {
            quoteId: `DESCARTES-${Date.now()}`,
            feeTotals: feeTotals,
            pricingStrategy: "DESCARTES",
        };

        setDuty(dutyValue);
        setDhlEstimation(descartesData);

        return { dhlData: descartesData, dutyValue };

    }, [formData, getDescartesToken, user, currencies, totalItemsValue, airfreight, insurance, collectionValue, totalDimensionsWeight, totalDimensionsLength, totalDimensionsWidth, totalDimensionsHeight, totalItemsQuantity, itemsTotalQty, calculateServiceValue]);

    // CRITICAL FIX: Calculate collection BEFORE CIF calculation
    useEffect(() => {
        if (hasEstimated && adminEstimate && adminEstimate.selectedTemplate && adminEstimate.selectedTemplate.services) {
            const collectionService = adminEstimate.selectedTemplate.services.find(
                service => service.description.toUpperCase() === 'COLLECTION'
            );
            if (collectionService) {
                const calculatedCollection = calculateServiceValue(
                    collectionService,
                    formData,
                    {},
                    totalItemsValue,
                    totalDimensionsWeight,
                    totalDimensionsLength,
                    totalDimensionsWidth,
                    totalDimensionsHeight,
                    0,
                    insurance,
                    totalItemsQuantity,
                    itemsTotalQty
                );
                setCollectionValue(calculatedCollection);
            } else {
                setCollectionValue(0);
            }
        }
    }, [
        hasEstimated,
        adminEstimate,
        formData,
        totalItemsValue,
        totalDimensionsWeight,
        totalDimensionsLength,
        totalDimensionsWidth,
        totalDimensionsHeight,
        insurance,
        totalItemsQuantity,
        itemsTotalQty
    ]);

    // Service, CIF, and Summary Calculation - FIXED ORDER
    useEffect(() => {
        if (!hasEstimated) {
            setEstimateSummary(null);
            setCalculatedValues({});
            return;
        }

        const calculatedCIF = (
            parseFloat(totalItemsValue || 0) +
            parseFloat(airfreight || 0) +
            parseFloat(insurance || 0) +
            parseFloat(duty || 0) +
            parseFloat(collectionValue || 0)
        );
        setCif(calculatedCIF);

        if (adminEstimate && adminEstimate.selectedTemplate && adminEstimate.selectedTemplate.services) {
            const newCalculatedValues = {};
            adminEstimate.selectedTemplate.services.forEach((service) => {
                newCalculatedValues[service.description] = calculateServiceValue(
                    service,
                    formData,
                    newCalculatedValues,
                    totalItemsValue,
                    totalDimensionsWeight,
                    totalDimensionsLength,
                    totalDimensionsWidth,
                    totalDimensionsHeight,
                    calculatedCIF,
                    insurance,
                    totalItemsQuantity,
                    itemsTotalQty
                );
            });
            setCalculatedValues(newCalculatedValues);

            const serviceTotal = Object.values(newCalculatedValues).reduce((sum, value) => sum + (parseFloat(value) || 0), 0);
            const dhlTaxTotal = dhlEstimation?.feeTotals?.reduce((sum, fee) => sum + fee.value, 0) || 0;
            const estimateTotal = serviceTotal + parseFloat(dhlTaxTotal || 0);

            let dhlTaxes = [];
            if (dhlEstimation && dhlEstimation.feeTotals) {
                const specificTaxes = ['DUTY', 'VAT', 'SALE', 'GST'];
                let otherTaxesTotal = 0;
                let otherTaxes = [];
                let mainTaxesMap = {};

                dhlEstimation.feeTotals.forEach(fee => {
                    if (specificTaxes.includes(fee.name)) {
                        mainTaxesMap[fee.name] = {
                            name: fee.name === 'SALE' || fee.name === 'GST' ? `VAT (${fee.name})` : fee.name,
                            value: fee.value
                        };
                    } else {
                        otherTaxes.push(fee);
                        otherTaxesTotal += fee.value;
                    }
                });

                specificTaxes.forEach(taxName => {
                    if (mainTaxesMap[taxName] && mainTaxesMap[taxName].value > 0) {
                        dhlTaxes.push(mainTaxesMap[taxName]);
                    }
                });

                if (otherTaxes.length > 0) {
                    dhlTaxes.push({ name: 'Taxes', value: otherTaxesTotal, details: otherTaxes });
                }
            }

            const updatedSummary = {
                total: estimateTotal,
                currency: user.currency || "USD", // Use user currency
                services: adminEstimate.selectedTemplate.services.map((service) => ({
                    description: service.description,
                    value: newCalculatedValues[service.description] || 0,
                })),
                dhlTaxes: dhlTaxes,
            };
            setEstimateSummary(updatedSummary);
        } else {
            const dhlTaxTotal = dhlEstimation?.feeTotals?.reduce((sum, fee) => sum + fee.value, 0) || 0;
            let dhlTaxes = [];

            if (dhlEstimation && dhlEstimation.feeTotals) {
                const specificTaxes = ['DUTY', 'VAT', 'SALE', 'GST'];
                let otherTaxesTotal = 0;
                let otherTaxes = [];
                let mainTaxesMap = {};

                dhlEstimation.feeTotals.forEach(fee => {
                    if (specificTaxes.includes(fee.name)) {
                        mainTaxesMap[fee.name] = {
                            name: fee.name === 'SALE' || fee.name === 'GST' ? `VAT (${fee.name})` : fee.name,
                            value: fee.value
                        };
                    } else {
                        otherTaxes.push(fee);
                        otherTaxesTotal += fee.value;
                    }
                });

                specificTaxes.forEach(taxName => {
                    if (mainTaxesMap[taxName] && mainTaxesMap[taxName].value > 0) {
                        dhlTaxes.push(mainTaxesMap[taxName]);
                    }
                });

                if (otherTaxes.length > 0) {
                    dhlTaxes.push({ name: 'Taxes', value: otherTaxesTotal, details: otherTaxes });
                }
            }

            setEstimateSummary({
                total: dhlTaxTotal,
                currency: user.currency || "USD", // Use user currency
                services: [],
                dhlTaxes: dhlTaxes,
            });
            setCalculatedValues({});
        }
    }, [
        hasEstimated,
        formData,
        adminEstimate,
        totalItemsValue,
        totalDimensionsWeight,
        totalDimensionsLength,
        totalDimensionsWidth,
        totalDimensionsHeight,
        totalItemsQuantity,
        itemsTotalQty,
        insurance,
        airfreight,
        duty,
        dhlEstimation,
        user.currency, // Use user currency
        collectionValue
    ]);

    // Form Validation
    const validateForm = () => {
        const newErrors = {};
        let hasErrors = false;

        const isEmpty = (value) => {
            if (value === null || value === undefined) return true;
            if (typeof value === "string") return value.trim() === "";
            if (typeof value === "number") return isNaN(value) || value === 0;
            return false;
        };

        const checkRequiredField = (key, msg) => {
            if (isEmpty(formData[key])) {
                newErrors[key] = msg;
                hasErrors = true;
            }
        };

        checkRequiredField("originCountry", "Origin country is required");
        checkRequiredField("destinationCountry", "Destination country is required");
        checkRequiredField("originCity", "Origin city is required");
        checkRequiredField("destinationCity", "Destination city is required");
        checkRequiredField("selectedService", "Service is required");
        checkRequiredField("itemsCurrency", "Items currency is required");
        checkRequiredField("insuranceSelected", "Insurance selection is required");

        if (needsStateSelection(formData.originCountry)) {
            checkRequiredField("originState", "Origin State is required");
        }
        if (needsStateSelection(formData.destinationCountry)) {
            checkRequiredField("destinationState", "Destination State is required");
        }

        if (formData.selectedService !== "EOR") {
            checkRequiredField("requiredDoc", "Import documentation status is required");
        }

        formData.dimensions.forEach((dimension, index) => {
            ['weight', 'length', 'width', 'height', 'quantity'].forEach(field => {
                if (isEmpty(dimension[field]?.value)) {
                    newErrors[`${field}-${index}`] = true;
                    hasErrors = true;
                }
            });
        });

        if (formData.items.length === 0) {
            newErrors.items = "At least one item is required.";
            hasErrors = true;
        } else {
            formData.items.forEach((item, index) => {
                [
                    "itemName",
                    "quantity",
                    "itemValue",
                    "countryOrigin",
                ].forEach((field) => {
                    if (isEmpty(item[field])) {
                        newErrors[`${field}-${index}`] = true;
                        hasErrors = true;
                    }
                });

                const hsStatus = hsCodeData[index]?.status;
                // HS Code validation: required (length >= 5) and status should not be 'invalid' or 'incomplete'.
                // If status is 'partially_correct', it's an error in validation to force user to confirm/select.
                const hsCodeInvalid = isEmpty(item.itemHSCode) || String(item.itemHSCode).length < 5 || hsStatus === "invalid" || hsStatus === "incomplete" || hsStatus === "partially_correct" || hsStatus === 'loading';

                if (hsCodeInvalid) {
                    newErrors[`itemHSCode-${index}`] = true;
                    hasErrors = true;
                }

                if ((formData.selectedService === "EOR" || formData.selectedService === "EOR & IOR")) {
                    if (isEmpty(item.itemEccnCode)) {
                        newErrors[`itemEccnCode-${index}`] = "ECCN Code is required.";
                        hasErrors = true;
                    } else {
                        const selectedEccnObj = eccnCodes.find(eccn => eccn.eccn_code === item.itemEccnCode);
                        if (selectedEccnObj && selectedEccnObj.restrictions) {
                            newErrors[`itemEccnCode-restricted-${index}`] = true;
                            hasErrors = true;
                        }
                    }
                }

                if (item.itemNew === false) {
                    newErrors[`itemNew-${index}`] = "Only new items are supported for automated estimates.";
                    hasErrors = true;
                }
            });
        }

        setErrors(newErrors);
        return hasErrors;
    };

    // --- Admin Notification Function (copied from EstimateForm) ---
    const createAdminNotification = async (
        estimateId,
        isUpdate = false
    ) => {
        try {
            const itemNames = formData.items.map(item => item.itemName);
            const formattedItemNames = itemNames.join(', ');
            const notificationData = {
                title: isUpdate ? "Estimate Update Request" : "New Estimate Request",
                message: `${user.name} has ${isUpdate ? "updated an" : "requested a new"} estimate for ${formattedItemNames}`,
                type: isUpdate ? "update-estimate" : "new-estimate",
                link: `/estimated-ior/${estimateId}`,
                linkText: "View Estimate",
                role: "admin",
            };

            return api.post(`${API_BASE_URL}/notifications`, notificationData);
        } catch (error) {
            console.error("Error creating admin notification:", error);
        }
    };

    const handleRequestOUS = async () => {
        if (!savedEstimateId) {
            toast.error("No estimate found to update. Please create an estimate first.");
            return;
        }

        try {
            setLoading(true);

            // Update formData with handling: "YES"
            const updatedFormData = {
                ...formData,
                handling: "YES"
            };

            // Recalculate everything with handling: "YES"
            // This ensures services are recalculated with the new handling value
            const calculatedCIF = (
                parseFloat(totalItemsValue || 0) +
                parseFloat(airfreight || 0) +
                parseFloat(insurance || 0) +
                parseFloat(duty || 0) +
                parseFloat(collectionValue || 0)
            );

            const newCalculatedValues = {};
            if (adminEstimate && adminEstimate.selectedTemplate && adminEstimate.selectedTemplate.services) {
                adminEstimate.selectedTemplate.services.forEach((service) => {
                    newCalculatedValues[service.description] = calculateServiceValue(
                        service,
                        updatedFormData, // Use updated form data
                        newCalculatedValues,
                        totalItemsValue,
                        totalDimensionsWeight,
                        totalDimensionsLength,
                        totalDimensionsWidth,
                        totalDimensionsHeight,
                        calculatedCIF,
                        insurance,
                        totalItemsQuantity,
                        itemsTotalQty
                    );
                });
            }

            const serviceTotal = Object.values(newCalculatedValues).reduce((sum, value) => sum + (parseFloat(value) || 0), 0);
            const dhlTaxTotal = dhlEstimation?.feeTotals?.reduce((sum, fee) => sum + fee.value, 0) || 0;
            const estimateTotal = serviceTotal + parseFloat(dhlTaxTotal || 0);

            let dhlTaxes = [];
            if (dhlEstimation && dhlEstimation.feeTotals) {
                const specificTaxes = ['DUTY', 'VAT', 'SALE', 'GST'];
                let otherTaxesTotal = 0;
                let otherTaxes = [];
                let mainTaxesMap = {};

                dhlEstimation.feeTotals.forEach(fee => {
                    if (specificTaxes.includes(fee.name)) {
                        mainTaxesMap[fee.name] = {
                            name: fee.name === 'SALE' || fee.name === 'GST' ? `VAT (${fee.name})` : fee.name,
                            value: fee.value
                        };
                    } else {
                        otherTaxes.push(fee);
                        otherTaxesTotal += fee.value;
                    }
                });

                specificTaxes.forEach(taxName => {
                    if (mainTaxesMap[taxName] && mainTaxesMap[taxName].value > 0) {
                        dhlTaxes.push(mainTaxesMap[taxName]);
                    }
                });

                if (otherTaxes.length > 0) {
                    dhlTaxes.push({ name: 'Taxes', value: otherTaxesTotal, details: otherTaxes });
                }
            }

            const updatedEstimateSummary = {
                total: estimateTotal,
                currency: user.currency || "USD",
                services: adminEstimate.selectedTemplate.services.map((service) => ({
                    description: service.description,
                    value: newCalculatedValues[service.description] || 0,
                })),
                dhlTaxes: dhlTaxes,
            };

            // Prepare complete update payload
            const updatePayload = {
                ...updatedFormData,
                dhlEstimation: dhlEstimation,
                estimateSummary: updatedEstimateSummary,
                estimationAccept: false, // Keep it as not accepted since it's being recalculated
                countryCodes: `${getAlpha2Code(formData.originCountry)}-${getAlpha2Code(formData.destinationCountry)}`,
                compliance: {
                    general: generalCompliance,
                    specific: specificCompliance
                }
            };

            // Call PUT API to update the estimate
            await api.put(`${API_BASE_URL}/clientestimates/id/${savedEstimateId}`, updatePayload);

            // Update local state
            setFormData(updatedFormData);
            setEstimateSummary(updatedEstimateSummary);
            setCalculatedValues(newCalculatedValues);

            // Show popup
            setShowRecalcPopup(true);

            toast.success("Estimate updated with OUS Handling successfully!");

        } catch (error) {
            console.error("Error updating estimate:", error);
            toast.error("Failed to update estimate. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // Combined submission logic (based on EstimateForm.jsx)
    const handleSubmit = async () => {
        if (readOnly || loading) return;

        if (hsValidationPending) {
            toast.error("Checking HS Codes. Please wait until validation completes.");
            return;
        }

        const hasValidationErrors = validateForm();
        if (hasValidationErrors) {
            const hasRestrictedEccn = formData.items.some(item => {
                if ((formData.selectedService === "EOR" || formData.selectedService === "EOR & IOR") && item.itemEccnCode) {
                    const selectedEccnObj = eccnCodes.find(eccn => eccn.eccn_code === item.itemEccnCode);
                    return selectedEccnObj && selectedEccnObj.restrictions;
                }
                return false;
            });

            if (hasRestrictedEccn) {
                setWarningPopup({
                    isOpen: true,
                    message: "The selected ECCN code is restricted. Please choose an unrestricted ECCN code or fill out a manual form. Contact our team at operations@oneunionsolutions.com for assistance.",
                    confirmText: "Got it"
                });
                return;
            }

            toast.error("Please fill all mandatory fields correctly.");
            return;
        }

        setLoading(true);
        setReadOnly(true);
        setHasEstimated(false);
        setLeadTimeData(null); // <--- FIX 1: Clear previous lead time data

        try {
            const isDisabled = disabledCountries.some(country =>
                country.countryName.toLowerCase() === formData.destinationCountry.toLowerCase()
            );
            if (isDisabled) {
                setWarningPopup({
                    isOpen: true,
                    message: `Currently Estimation not Available for ${formData.destinationCountry}. Please contact our team through operations@oneunionsolutions.com with your request or fill the form manually.`
                });
                return;
            }

            if ((formData.selectedService === "EOR" || formData.selectedService === "EOR & IOR") && formData.eccnCode) {
                const selectedEccnObj = eccnCodes.find(eccn => eccn.eccn_code === formData.eccnCode);
                if (selectedEccnObj && selectedEccnObj.restrictions) {
                    setWarningPopup({
                        isOpen: true,
                        message: "The selected ECCN code is restricted. You must fill out a manual form. Please contact our team at operations@oneunionsolutions.com for assistance."
                    });
                    return;
                }
            }

            // Fetch admin estimate and wait for it
            const currentAdminEstimate = await fetchAdminEstimate(
                formData.originCountry,
                formData.destinationCountry,
                formData.selectedService,
                formData.itemsCurrency
            );

            if (!currentAdminEstimate) {
                setWarningPopup({
                    isOpen: true,
                    message: `Estimation not found for ${formData.destinationCountry}. Please contact our team through operations@oneunionsolutions.com with your request or fill the form manually.`,
                    confirmText: "Got it",
                    onConfirm: () => {
                        setWarningPopup({ isOpen: false, message: "" });
                    }
                });
                return; // Stop execution
            }

            let dutyValue = 0;
            let dhlResult = null;
            const isDutyVatDisabled = disabledDutyVatCountries.some(country =>
                country.countryName.toLowerCase() === formData.destinationCountry.toLowerCase()
            );

            if (formData.selectedService !== "EOR" && !isDutyVatDisabled) {
                // dhlResult = await getDHLEstimation(formData.items);
                dhlResult = await getDescartesEstimation(formData.items, currentAdminEstimate);
                dutyValue = dhlResult?.dutyValue || 0;

                // Check if VAT or Duty was returned
                const hasVatOrDuty = dhlResult?.dhlData?.feeTotals?.some(
                    (fee) =>
                        fee.name === "DUTY" ||
                        fee.name === "VAT" ||
                        fee.name?.includes("VAT") ||
                        fee.name === "Taxes"
                );

                if (!hasVatOrDuty) {
                    setWarningPopup({
                        isOpen: true,
                        title: "VAT & Duty Unavailable",
                        subtitle: "",
                        message: "VAT and Duty are currently unavailable for this estimation due to a temporary server issue. Our team is working to restore the service as quickly as possible.",
                        confirmText: "Ok",
                    });
                }
            }
            setDuty(dutyValue);

            // Set hasEstimated to true *before* calculating summary
            setHasEstimated(true);

            // Run compliance calls AND Lead Times
            await Promise.all([
                getGeneralCompliance(),
                getSpecificCompliance(),
                fetchLeadTimes(formData.destinationCountry) // <--- FIX 2: Call the function here
            ]);

            // --- Prepare data for saving ---
            // Recalculate CIF and Summary based on all data
            const calculatedInsurance = formData.insuranceSelected === 'YES' ? totalItemsValue * 0.0075 : 0;
            const calculatedCollection = 0; // Will be set by useEffect, but for safety
            const calculatedAirfreight = airfreight; // Already calculated by useEffect

            const calculatedCIF = (
                parseFloat(totalItemsValue || 0) +
                parseFloat(calculatedAirfreight || 0) +
                parseFloat(calculatedInsurance || 0) +
                parseFloat(dutyValue || 0) +
                parseFloat(calculatedCollection || 0) // Use placeholder for now
            );

            const newCalculatedValues = {};
            if (currentAdminEstimate && currentAdminEstimate.selectedTemplate && currentAdminEstimate.selectedTemplate.services) {
                currentAdminEstimate.selectedTemplate.services.forEach((service) => {
                    newCalculatedValues[service.description] = calculateServiceValue(
                        service,
                        formData,
                        newCalculatedValues,
                        totalItemsValue,
                        totalDimensionsWeight,
                        totalDimensionsLength,
                        totalDimensionsWidth,
                        totalDimensionsHeight,
                        calculatedCIF, // Pass calculated CIF
                        calculatedInsurance,
                        totalItemsQuantity,
                        itemsTotalQty
                    );
                });
            }

            const serviceTotal = Object.values(newCalculatedValues).reduce((sum, value) => sum + (parseFloat(value) || 0), 0);
            const dhlTaxTotal = dhlResult?.dhlData?.feeTotals?.reduce((sum, fee) => sum + fee.value, 0) || 0;
            const estimateTotal = serviceTotal + parseFloat(dhlTaxTotal || 0);

            let dhlTaxes = [];
            if (dhlResult?.dhlData && dhlResult.dhlData.feeTotals) {
                // ... (dhlTaxes calculation logic from useEffect)
                const specificTaxes = ['DUTY', 'VAT', 'SALE', 'GST'];
                let otherTaxesTotal = 0;
                let otherTaxes = [];
                let mainTaxesMap = {};

                dhlResult.dhlData.feeTotals.forEach(fee => {
                    if (specificTaxes.includes(fee.name)) {
                        mainTaxesMap[fee.name] = {
                            name: fee.name === 'SALE' || fee.name === 'GST' ? `VAT (${fee.name})` : fee.name,
                            value: fee.value
                        };
                    } else {
                        otherTaxes.push(fee);
                        otherTaxesTotal += fee.value;
                    }
                });
                specificTaxes.forEach(taxName => {
                    if (mainTaxesMap[taxName] && mainTaxesMap[taxName].value > 0) {
                        dhlTaxes.push(mainTaxesMap[taxName]);
                    }
                });
                if (otherTaxes.length > 0) {
                    dhlTaxes.push({ name: 'Taxes', value: otherTaxesTotal, details: otherTaxes });
                }
            }

            const finalEstimateSummary = {
                total: estimateTotal,
                currency: user.currency || "USD",
                services: currentAdminEstimate.selectedTemplate.services.map((service) => ({
                    description: service.description,
                    value: newCalculatedValues[service.description] || 0,
                })),
                dhlTaxes: dhlTaxes,
            };

            // Set the summary for display
            setEstimateSummary(finalEstimateSummary);

            const estimateData = {
                ...formData, // Spread all form data
                // Ensure user data from form (which is from context) is used
                userId: formData.userId,
                userName: formData.userName,
                userCity: formData.userCity,
                userCountry: formData.userCountry,
                postalCode: formData.postalCode,
                email: formData.email,
                // Add calculation results
                dhlEstimation: dhlResult?.dhlData || null,
                estimateSummary: finalEstimateSummary,
                estimationAccept: false, // Default to false
                countryCodes: `${getAlpha2Code(formData.originCountry)}-${getAlpha2Code(formData.destinationCountry)}`,
                // Add compliance data
                compliance: {
                    general: generalCompliance,
                    specific: specificCompliance
                }
            };

            // --- Post to /clientestimates ---
            const adminsPromise = getAllSuperAdminUsers();

            // Post the new estimate
            const response = await api.post(`${API_BASE_URL}/clientestimates`, estimateData);
            const estimateId = response.data.data.estimateId;

            setSavedEstimateId(estimateId);

            // Create notifications and send emails
            const notificationPromise = createAdminNotification(estimateId, false);
            const [admins] = await Promise.all([adminsPromise, notificationPromise]);

            const adminEmails = admins.map((admin) => admin.email);
            const link = `${API_DEPLOYED_URL}/estimated-ior/${estimateId}`;
            await api.post(`${API_BASE_URL}/mail/estimate-notification`, {
                estimateData,
                isUpdate: false, // This is a new estimate
                adminEmails,
                link,
            });

            toast.success("Estimate and Compliance results ready!");

        } catch (error) {
            // Reset state on failure
            setHasEstimated(false);
            toast.error(`Failed to process request: ${error.message}`);
        } finally {
            setLoading(false);
            setReadOnly(false); // Set to false so user can see results, button logic will handle hiding
        }
    };


    const getGeneralCompliance = async () => {
        try {
            const token = await getComplianceToken();
            if (!token) {
                setGeneralCompliance([]);
                return;
            }

            const res = await fetch(`${COMPLIANCE_API_BASE_URL}/api/full-compliance`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    countryCode: getCountryCodeByName(formData.destinationCountry),
                    HSCode: formData.items[0]?.itemHSCode || '000000',
                })
            })

            const data = await res.json();
            const genComp = data.general?.compliance?.filter(i => i.new_label2 === "Shipment" && (i.new_label1 === "All modes" || i.new_label1 === "Air Freight")) || [];
            setGeneralCompliance(genComp);

        } catch (error) {
            console.error("Error fetching general compliance:", error);
            setGeneralCompliance([]);
        }
    }

    const getSpecificCompliance = async () => {
        try {
            const token = await getComplianceToken();
            if (!token) {
                setSpecificCompliance([]);
                return;
            }

            const itemData = formData.items;
            const hsCodes = Array.from(new Set(itemData.map(i => String(i.itemHSCode || '').trim()).filter(Boolean)));

            const apiData = hsCodes.map((hs) => ({
                countryCode: getCountryCodeByName(formData.destinationCountry),
                HSCode: hs,
            }));

            const res = await Promise.all(apiData.map(async (item) => {
                const response = await fetch(`${COMPLIANCE_API_BASE_URL}/api/full-compliance`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify(item)
                })

                return response.json();
            }))

            const data = res.map((resp) => {
                const respHs = String(resp.HSCode || '').trim();
                const matchingItems = itemData.filter(i => String(i.itemHSCode || '').trim() === respHs);

                return {
                    HSCode: respHs,
                    compliance: resp.specific?.compliance || [],
                    items: matchingItems,
                };
            }).filter(d => d.compliance.length > 0);

            setSpecificCompliance(data);

        } catch (error) {
            console.error("Error fetching specific compliance:", error);
            setSpecificCompliance([]);
        }
    }

    return (
        <>
            <div className="w-full h-full p-10 space-y-6">
                <div className="w-full h-full flex flex-col md:flex-row items-start justify-center gap-6">
                    <div className="md:w-1/2 w-full h-full space-y-6">
                        <FormCard title="One Union – Cost & Compliance" renderTag={uploadButton}>
                            <IorFormCard
                                formData={formData}
                                setFormData={setFormData}
                                errors={errors}
                                setErrors={setErrors}
                                handleSubmit={handleSubmit} // Pass the new handleSubmit
                                isReadOnly={readOnly || loading}
                                // Remove all customer props
                                eccnCodes={eccnCodes}
                                handleInputChange={handleInputChange}
                                hsCodeData={hsCodeData}
                                handleCheckHsCode={handleCheckHsCode}
                                setHsCodeData={setHsCodeData}
                                hsValidationPending={hsValidationPending}
                                hasEstimated={hasEstimated} // Pass this for button logic
                                onRequestOUS={handleRequestOUS} // PASS THIS
                                showRecalcPopup={showRecalcPopup} // PASS THIS
                                setShowRecalcPopup={setShowRecalcPopup} // PASS THIS
                            />
                        </FormCard>

                        {hasEstimated && leadTimeData && (
                            <FormCard title="Lead Time">
                                <LeadTimeCard data={leadTimeData} country={formData.destinationCountry} />
                            </FormCard>
                        )}
                    </div>
                    <div className="w-full md:w-1/2 flex flex-col items-center justify-center gap-6">
                        <FormCard title="Duty & Tax Orbit" renderTag={() => <p>{formData?.destinationCountry}</p>}>
                            <DutyOrbitCard formData={formData} estimateSummary={estimateSummary} totalItemsValue={totalItemsValue} />
                        </FormCard>

                        <FormCard title={`${formData.selectedService} Cost`} className="min-h-[200px]">
                            {loading ? (
                                <div className="w-full h-full flex items-center justify-center min-h-[150px]">
                                    <Loader />
                                </div>
                            ) : (
                                <IorCostCard
                                    formData={formData}
                                    estimateSummary={estimateSummary}
                                    totalItemsValue={totalItemsValue}
                                    airfreight={airfreight}
                                    insurance={insurance}
                                    cif={cif}
                                    duty={duty}
                                    collectionValue={collectionValue}
                                    hasEstimated={hasEstimated}
                                />
                            )}
                        </FormCard>
                    </div>
                </div>

                {loading && (
                    <FormCard title="Specific Compliance" className="min-h-[200px]">
                        <div className="w-full h-full flex items-center justify-center min-h-[150px]">
                            <Loader />
                        </div>
                    </FormCard>
                )}
                {specificCompliance.some(item => item.compliance.length) > 0 && hasEstimated && !loading && (
                    <FormCard
                        title="Specific Compliance"
                        renderTag={() => (
                            <button
                                className=" btnBg rounded-lg px-4 py-2 mt-2 text-black bg-gray-200"
                                onClick={handleRequestOUS} // CHANGE THIS
                            >
                                Request OUS Handling
                            </button>
                        )}>
                        <CompliancePassportCard
                            data={specificCompliance}
                            onRequestOUS={handleRequestOUS} // PASS THIS
                            formData={formData} // PASS THIS
                            setFormData={setFormData} // PASS THIS
                        />
                    </FormCard>
                )}

                {loading && (
                    <FormCard title="General Compliance" className="min-h-[200px]">
                        <div className="w-full h-full flex items-center justify-center min-h-[150px]">
                            <Loader />
                        </div>
                    </FormCard>
                )}
                {generalCompliance.length > 0 && hasEstimated && !loading && (
                    <FormCard title="General Compliance">
                        <GeneralComplianceCard data={generalCompliance} />
                    </FormCard>
                )}
            </div >

            {warningPopup.isOpen && (
                <div
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[999999] p-4"
                    onClick={() => {
                        setWarningPopup({ isOpen: false, message: '' });
                        setReadOnly(false);
                        setLoading(false);
                    }}
                >
                    <div
                        className="bg-white rounded-2xl shadow-2xl transform transition-all duration-300 ease-out"
                        style={{
                            maxWidth: '600px',
                            width: '100%',
                            animation: 'slideIn 0.3s ease-out'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="bg-gradient-to-r from-orange-600 to-orange-400 rounded-t-2xl p-6 relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -mr-20 -mt-20"></div>
                            <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/10 rounded-full -ml-16 -mb-16"></div>

                            <div className="relative flex items-center gap-4">
                                <div className="flex-shrink-0 w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center border-2 border-white/30">
                                    <svg
                                        width="32"
                                        height="32"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M12 9v4m0 4h.01" />
                                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                                    </svg>
                                </div>

                                <div className="flex-1">
                                    <h3 className="text-2xl font-bold text-white mb-1">
                                        {warningPopup.title || "Estimation Not Available"}
                                    </h3>
                                    <p className="text-orange-50 text-sm">
                                        {warningPopup.subtitle !== undefined ? warningPopup.subtitle : "We're here to help you proceed"}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="p-8">
                            <div className="bg-orange-50 border-l-4 border-orange-400 rounded-r-lg p-4 mb-6">
                                <p className="text-gray-700 leading-relaxed">
                                    {warningPopup.message}
                                </p>
                            </div>

                            <div className="bg-gray-50 rounded-lg p-4 mb-6">
                                <p className="text-sm text-gray-600 mb-2 font-medium">
                                    Contact our operations team:
                                </p>
                                <a
                                    href="mailto:operations@oneunionsolutions.com"
                                    className="text-orange-600 hover:text-orange-700 font-semibold text-sm flex items-center gap-2 transition-colors"
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                                        <polyline points="22,6 12,13 2,6" />
                                    </svg>
                                    operations@oneunionsolutions.com
                                </a>
                            </div>

                            <div className="flex flex-col sm:flex-row gap-3">
                                <button
                                    onClick={() => {
                                        if (warningPopup.onConfirm) {
                                            warningPopup.onConfirm();
                                        }
                                        setWarningPopup({ isOpen: false, message: '' });
                                        setReadOnly(false);
                                        setLoading(false);
                                    }}
                                    className="flex-1 shine bg-gradient-to-r from-orange-600 to-orange-400 text-white shadow-md shadow-orange-300 hover:shadow-xl hover:shadow-orange-300 transition-all duration-300 ease-in-out rounded-lg px-6 py-3 font-semibold text-sm flex items-center justify-center gap-2 transform hover:scale-[1.02]"
                                >
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                    {warningPopup.confirmText || 'Got It'}
                                </button>

                                <button
                                    onClick={() => {
                                        setWarningPopup({ isOpen: false, message: '' });
                                        setReadOnly(false);
                                        setLoading(false);
                                    }}
                                    className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg px-6 py-3 font-semibold text-sm transition-all duration-200 transform hover:scale-[1.02]"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showRecalcPopup && (
                <div
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[999999] p-4"
                    onClick={() => setShowRecalcPopup(false)}
                >
                    <div
                        className="bg-white rounded-2xl shadow-2xl transform transition-all duration-300 ease-out"
                        style={{
                            maxWidth: '600px',
                            width: '100%',
                            animation: 'slideIn 0.3s ease-out'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="bg-gradient-to-r from-orange-600 to-orange-400 rounded-t-2xl p-6 relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -mr-20 -mt-20"></div>
                            <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/10 rounded-full -ml-16 -mb-16"></div>

                            <div className="relative flex items-center gap-4">
                                <div className="flex-shrink-0 w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center border-2 border-white/30">
                                    <svg
                                        width="32"
                                        height="32"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M12 9v4m0 4h.01" />
                                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                                    </svg>
                                </div>

                                <div className="flex-1">
                                    <h3 className="text-2xl font-bold text-white mb-1">
                                        Recalculating Quotation
                                    </h3>
                                    <p className="text-orange-50 text-sm">
                                        Processing your request
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="p-8">
                            <div className="bg-orange-50 border-l-4 border-orange-400 rounded-r-lg p-4 mb-6">
                                <p className="text-gray-700 leading-relaxed">
                                    We are recalculating the quotation according to One Union Solutions end to end compliance for shipment.
                                </p>
                            </div>

                            <div className="flex justify-center">
                                <button
                                    onClick={() => setShowRecalcPopup(false)}
                                    className="w-full bg-gradient-to-r from-orange-600 to-orange-400 text-white shadow-md shadow-orange-300 hover:shadow-xl hover:shadow-orange-300 transition-all duration-300 ease-in-out rounded-lg px-6 py-3 font-semibold text-sm flex items-center justify-center gap-2 transform hover:scale-[1.02]"
                                >
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                    Got It
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <style jsx>{`
                @keyframes slideIn {
                    from {
                        opacity: 0;
                        transform: translateY(-20px) scale(0.95);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0) scale(1);
                    }
                }
            `}</style>
        </>
    )
}

export default IorCalculatorForm

